import type { TFunction } from 'i18next';
import { useState } from 'react';

import { MFA_FACTOR, MFA_TOTP_CONTRACT, type MfaFactor } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSInputLabel } from '@repo/sds/SDSInputLabel';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSSeparator } from '@repo/sds/SDSSeparator';
import { SDSText } from '@repo/sds/SDSText';

import authenticatorAppIntro from './assets/authenticator-app-intro.svg';
import { MfaHero } from './mfa-hero';
import { MfaInvalidCodeDialog } from './mfa-invalid-code-dialog';
import { MfaOtpInput } from './mfa-otp-input';
import { MfaSettingsPage } from './mfa-settings-page';
import { TotpQrCode } from './totp-qr-code';

const MFA = DataTestIds.COMMON.SECURITY.MFA;
const TOTP_CODE_DIGITS = MFA_TOTP_CONTRACT.digits;
const TOTP_INTRO_APPS = [
  'totpIntroGoogle',
  'totpIntroMicrosoft',
  'totpIntroApple',
  'totpIntroOnePassword',
  'totpIntroDuo',
] as const;
const TOTP_VERIFY_STEPS = ['totpStepOpen', 'totpStepAdd', 'totpStepScan', 'totpStepEnter'] as const;

function formatTotpSecret(secret: string): string {
  return secret
    .replace(/\s+/g, '')
    .toUpperCase()
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

function TotpAppList({ lead, t }: { lead: string; t: TFunction<'common'> }) {
  return (
    <div className="MfaTotpDialog__introBody">
      <SDSText text={lead} type="body-small" />
      <ul className="MfaTotpDialog__apps">
        {TOTP_INTRO_APPS.map(key => (
          <li key={key}>
            <SDSText text={t(`security.mfa.${key}`)} type="body-small" />
          </li>
        ))}
      </ul>
    </div>
  );
}

function TotpQrOrSecret({
  otpauthUri,
  secret,
  showManualKey,
  title,
  toggleLabel,
  onToggleManual,
  t,
}: {
  otpauthUri: string;
  secret: string;
  showManualKey: boolean;
  title: string;
  toggleLabel: string;
  onToggleManual: () => void;
  t: TFunction<'common'>;
}) {
  return (
    <SDSFlexbox alignItems="center" direction="column" gap="text-medium">
      <SDSText align="center" text={title} type="body-medium-bold" />
      {showManualKey ? (
        <SDSText
          align="center"
          color="text-secondary"
          dataTestId={MFA.SECRET}
          text={formatTotpSecret(secret)}
          type="body-medium"
        />
      ) : (
        <div className="MfaTotpDialog__qrWrap">
          <TotpQrCode
            className="MfaTotpDialog__qr"
            dataTestId={MFA.QR}
            label={t('security.mfa.qrLabel')}
            otpauthUri={otpauthUri}
          />
        </div>
      )}
      <SDSPrimitiveButton className="MfaTotpDialog__link" dataTestId={MFA.TOTP_MANUAL} onClick={onToggleManual}>
        <SDSText color="primary" text={toggleLabel} type="body-medium" />
      </SDSPrimitiveButton>
    </SDSFlexbox>
  );
}

function TotpVerifySteps({ className, t }: { className?: string; t: TFunction<'common'> }) {
  return (
    <ol className={className ? `MfaTotpDialog__steps ${className}` : 'MfaTotpDialog__steps'}>
      {TOTP_VERIFY_STEPS.map((key, index) => (
        <li key={key} className="MfaTotpDialog__step">
          <span className="MfaTotpDialog__stepIndex">{index + 1}</span>
          <SDSText text={t(`security.mfa.${key}`)} type="body-small" />
        </li>
      ))}
    </ol>
  );
}

export function MfaTotpSetupDialogs({
  actionError,
  busy,
  code,
  isMobile,
  onBackToMethods,
  onCancel,
  onChooseMethod,
  onClearActionError,
  onConfirmSetup,
  onSetupCodeChange,
  otpauthUri,
  secret,
  t,
}: {
  actionError?: string;
  busy: boolean;
  code: string;
  isMobile: boolean;
  onBackToMethods: () => void;
  onCancel: () => void;
  onChooseMethod: (method: MfaFactor) => void;
  onClearActionError: () => void;
  onConfirmSetup: () => void;
  onSetupCodeChange: (value: string) => void;
  otpauthUri: string;
  secret: string;
  t: TFunction<'common'>;
}) {
  const [phase, setPhase] = useState<'intro' | 'verify' | 'invalid'>('intro');
  const [showManualKey, setShowManualKey] = useState(false);

  // Any action error routes this dialog to the invalid-code screen, whatever
  // phase it was in. Deriving it here (instead of syncing `phase` to
  // `actionError` in an effect) means there's nothing to re-sync: the parent
  // clears `actionError` before it starts a fresh attempt, so this can't get
  // stuck showing an error for an attempt that has already moved on.
  const effectivePhase = actionError ? 'invalid' : phase;

  if (effectivePhase === 'invalid') {
    return (
      <MfaInvalidCodeDialog
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.TOTP_INVALID}
        description={t('security.mfa.totpInvalidDescription')}
        isMobile={isMobile}
        onClose={onCancel}
        primaryAction={{
          dataTestId: MFA.TOTP_TRY_AGAIN,
          label: t('security.mfa.totpTryAgain'),
          onClick: () => {
            onSetupCodeChange('');
            onClearActionError();
            setPhase('verify');
          },
        }}
        secondaryAction={{
          dataTestId: MFA.TOTP_SETUP_AGAIN,
          label: t('security.mfa.totpSetupAgain'),
          onClick: () => {
            onSetupCodeChange('');
            onClearActionError();
            setPhase('intro');
            onChooseMethod(MFA_FACTOR.Totp);
          },
        }}
        title={t('security.mfa.totpInvalidTitle')}
      />
    );
  }

  const qrOrSecret = (
    <TotpQrOrSecret
      otpauthUri={otpauthUri}
      secret={secret}
      showManualKey={showManualKey}
      t={t}
      title={isMobile && showManualKey ? t('security.mfa.totpTypeTextCode') : t('security.mfa.totpScanQr')}
      toggleLabel={isMobile && showManualKey ? t('security.mfa.totpScanQrInstead') : t('security.mfa.totpCantScan')}
      onToggleManual={() => setShowManualKey(current => !current)}
    />
  );

  if (isMobile) {
    return (
      <MfaSettingsPage
        actions={
          <>
            <SDSButton
              dataTestId={MFA.TOTP_BACK}
              mode="outline"
              onClick={onBackToMethods}
              text={t('security.mfa.totpBack')}
            />
            <SDSButton
              dataTestId={MFA.CONFIRM}
              isDisabled={!busy && code.length !== TOTP_CODE_DIGITS}
              isSubmitting={busy}
              onClick={onConfirmSetup}
              text={t('security.mfa.totpVerify')}
            />
          </>
        }
        ariaLabel={t('security.mfa.totpIntroTitle')}
        backLabel={t('security.mfa.backToSecurity')}
        dataTestId={MFA.SETUP_DIALOG}
        fieldsGap="text-large"
        onBack={onCancel}
      >
        <SDSFlexbox alignItems="center" direction="column" fullWidth gap="text-medium">
          <SDSText text={t('security.mfa.totpIntroTitle')} type="h5" />
          <MfaHero plain setup src={authenticatorAppIntro} />
        </SDSFlexbox>
        <TotpAppList lead={t('security.mfa.totpIntroLeadMobile')} t={t} />
        <SDSSeparator />
        {qrOrSecret}
        <SDSSeparator />
        <TotpVerifySteps className="MfaTotpDialog__steps--page" t={t} />
        <SDSSeparator />
        <SDSFlexbox direction="column" fullWidth gap="text-small">
          <SDSInputLabel isRequired label={t('security.mfa.totpMobileCodeLabel')} />
          <MfaOtpInput onChange={onSetupCodeChange} value={code} />
        </SDSFlexbox>
      </MfaSettingsPage>
    );
  }

  if (phase === 'intro') {
    return (
      <SDSDialog
        className="MfaTotpDialog"
        dataTestId={MFA.SETUP_DIALOG}
        isOpen
        onClose={onCancel}
        primaryAction={{
          dataTestId: MFA.TOTP_INTRO_CONTINUE,
          label: t('security.mfa.continue'),
          onClick: () => setPhase('verify'),
        }}
        secondaryAction={{ dataTestId: MFA.CANCEL, label: t('security.mfa.totpClose'), onClick: onCancel }}
        showCloseButton={false}
        size="small"
      >
        <SDSFlexbox direction="column" gap="text-x-large">
          <MfaHero setup src={authenticatorAppIntro} />
          <SDSFlexbox direction="column" gap="text-medium">
            <SDSText text={t('security.mfa.totpIntroTitle')} type="h5" />
            <TotpAppList lead={t('security.mfa.totpIntroLead')} t={t} />
          </SDSFlexbox>
        </SDSFlexbox>
      </SDSDialog>
    );
  }

  return (
    <SDSDialog
      className="MfaTotpDialog"
      dataTestId={MFA.SETUP_DIALOG}
      isOpen
      onClose={onCancel}
      primaryAction={{
        dataTestId: MFA.CONFIRM,
        disabled: !busy && code.length !== TOTP_CODE_DIGITS,
        loading: busy,
        label: t('security.mfa.totpVerify'),
        onClick: onConfirmSetup,
      }}
      secondaryAction={{
        dataTestId: MFA.TOTP_BACK,
        label: t('security.mfa.totpBack'),
        onClick: () => setPhase('intro'),
      }}
      size="small"
      title={t('security.mfa.totpVerifyTitle')}
    >
      <SDSFlexbox direction="column" gap="text-x-large">
        <TotpVerifySteps t={t} />
        {qrOrSecret}
        <SDSFlexbox direction="column" gap="text-small">
          <SDSInputLabel isRequired label={t('security.mfa.totpSixDigitLabel')} />
          <MfaOtpInput onChange={onSetupCodeChange} value={code} />
        </SDSFlexbox>
      </SDSFlexbox>
    </SDSDialog>
  );
}
